import { useEffect, useState } from "react";
import { Sparkles, Loader2, FolderSearch } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import MicButton from "@/components/ui/MicButton";
import { fetchDocuments, labelDocument } from "@/lib/dataroom";
import { categorize } from "@/components/dataroom/DataroomSection";

const LABELS = {
    revenus: { title: "Pré-remplir les revenus", hint: "L'IA lit la balance (comptes 70x, top clients 411) et les documents commerciaux pour proposer une ligne par client / flux.", ph: "Ex : Ne garder que les 5 plus gros clients" },
    charges_expl: { title: "Pré-remplir les charges d'exploitation", hint: "L'IA propose les achats et charges variables (60x, sous-traitance, honoraires) depuis la balance détaillée.", ph: "Ex : Uniquement la sous-traitance et les achats de marchandises" },
    charges_fixes: { title: "Pré-remplir les charges fixes", hint: "L'IA identifie loyers, assurances, abonnements, échéances d'emprunt… et les mensualise.", ph: "Ex : Extrais uniquement les contrats de loyer" },
    salaires: { title: "Pré-remplir la masse salariale", hint: "L'IA lit les fiches de paie / documents RH et propose une ligne par salarié (nom, brut mensuel).", ph: "Ex : Garde uniquement Davou, Villey et Douillard" },
};

// HOTFIX 120 — groupement visuel des sources par section de la Dataroom (identique à l'onglet Dataroom).
const CAT_ORDER = ["pv_jugement", "administratif", "banques", "comptable", "juridique", "autres"];
const CAT_TITLES = { pv_jugement: "PV et Jugements", administratif: "Administratifs & dirigeant", banques: "Banques", comptable: "Comptables", juridique: "Juridiques", autres: "Autres pièces" };

/** Bouton « Pré-remplir depuis la Dataroom » : sélection multi-sources (HOTFIX 120) → instruction → IA → lignes ajoutées au state. */
export default function MagicFillButton({ section, onExtract, onLignes, disabled, testId, dossierId }) {
    const [open, setOpen] = useState(false);
    const [instruction, setInstruction] = useState("");
    const [busy, setBusy] = useState(false);
    const [prog, setProg] = useState(null);
    const [docs, setDocs] = useState([]);
    const [docsLoading, setDocsLoading] = useState(false);
    const [selected, setSelected] = useState(() => new Set());
    const L = LABELS[section] || LABELS.revenus;
    const hasSelector = !!dossierId;

    useEffect(() => {
        if (!open || !dossierId || docs.length) return;
        setDocsLoading(true);
        fetchDocuments(dossierId)
            .then((all) => setDocs((all || []).filter((d) => d.inclure_analyse !== false)))
            .catch(() => setDocs([]))
            .finally(() => setDocsLoading(false));
    }, [open, dossierId, docs.length]);

    const toggleDoc = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
    const groups = CAT_ORDER
        .map((key) => ({ key, title: CAT_TITLES[key], items: docs.filter((d) => categorize(d) === key) }))
        .filter((g) => g.items.length);

    async function run() {
        setBusy(true);
        setProg(null);
        try {
            const docIds = [...selected];
            const r = await onExtract(section, instruction.trim() || null, docIds, (done, total) => setProg({ done, total }));
            const added = onLignes(r.lignes || []);
            setOpen(false);
            if (!r.lignes?.length) toast.warning("Aucune ligne identifiée", { description: "Vérifiez que les documents (FEC, balance, contrats, paie) sont déposés et extraits dans la Dataroom." });
            else toast.success(`${added} ligne${added > 1 ? "s" : ""} ajoutée${added > 1 ? "s" : ""}`, { description: `${r.lignes.length} proposée${r.lignes.length > 1 ? "s" : ""} par l'IA depuis ${(r.documents || []).length} document(s)${r.source === "rh" ? " RH" : ""}${added < r.lignes.length ? " · doublons ignorés" : ""}` });
        } catch (e) { toast.error("Échec du pré-remplissage (Magic Fill)", { description: e?.message || "Erreur inconnue du serveur — réessayez.", duration: 12000 }); }
        finally { setBusy(false); setProg(null); }
    }

    const pct = prog?.total ? Math.round((prog.done / prog.total) * 100) : null;

    return (
        <>
            <Button type="button" variant="outline" size="sm" data-testid={testId} disabled={disabled} onClick={() => setOpen(true)} className="rounded-full border-indigo-300 text-indigo-700 hover:bg-indigo-50">
                <Sparkles className="h-4 w-4" /> Pré-remplir depuis la Dataroom
            </Button>
            <Dialog open={open} onOpenChange={(o) => !busy && setOpen(o)}>
                <DialogContent data-testid={`${testId}-dialog`} className="sm:max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-indigo-600" /> {L.title}</DialogTitle>
                        <DialogDescription>{L.hint} Vous pourrez corriger les montants ensuite.</DialogDescription>
                    </DialogHeader>

                    {hasSelector && (
                        <div className="space-y-2" data-testid={`${testId}-sources`}>
                            <div className="flex items-center justify-between">
                                <label className="text-sm font-medium text-slate-700 flex items-center gap-1.5"><FolderSearch className="h-4 w-4 text-indigo-500" /> Documents sources à analyser</label>
                                {selected.size > 0 && (
                                    <button type="button" data-testid={`${testId}-sources-clear`} onClick={() => setSelected(new Set())} disabled={busy} className="text-xs font-medium text-indigo-600 hover:underline">Tout décocher</button>
                                )}
                            </div>
                            <div className="max-h-52 overflow-y-auto rounded-lg border border-slate-200 bg-slate-50 p-2">
                                {docsLoading ? (
                                    <div className="flex items-center gap-2 px-2 py-3 text-xs text-slate-500"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Chargement des documents de la Dataroom…</div>
                                ) : !groups.length ? (
                                    <p className="px-2 py-3 text-xs text-slate-500">Aucun document analysable dans la Dataroom.</p>
                                ) : groups.map((g) => (
                                    <div key={g.key} className="mb-1.5 last:mb-0">
                                        <p className="px-1 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">{g.title}</p>
                                        {g.items.map((d) => (
                                            <label key={d.id} data-testid={`${testId}-source-${d.id}`} className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm text-slate-700 hover:bg-white cursor-pointer">
                                                <Checkbox checked={selected.has(d.id)} onCheckedChange={() => toggleDoc(d.id)} disabled={busy} className="border-slate-300 data-[state=checked]:bg-indigo-600 data-[state=checked]:border-indigo-600" />
                                                <span className="min-w-0 flex-1 truncate" title={d.nom_fichier}>{d.nom_fichier || "Document"}</span>
                                                <span className="shrink-0 text-[11px] text-slate-400">{d.type_document === "autre" && d.type_personnalise ? d.type_personnalise : labelDocument(d.type_document)}</span>
                                            </label>
                                        ))}
                                    </div>
                                ))}
                            </div>
                            {!docsLoading && selected.size === 0 && (
                                <p data-testid={`${testId}-sources-empty-hint`} className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800">Aucun fichier ciblé : l&apos;IA scannera l&apos;intégralité de la Dataroom.</p>
                            )}
                        </div>
                    )}

                    <div className="space-y-1.5">
                        <label htmlFor={`${testId}-instruction`} className="text-sm font-medium text-slate-700">Instructions optionnelles</label>
                        <div className="relative">
                            <Textarea id={`${testId}-instruction`} data-testid={`${testId}-instruction`} value={instruction} onChange={(e) => setInstruction(e.target.value)} rows={3} className="min-h-[80px] pr-12" placeholder={L.ph} disabled={busy} />
                            <MicButton testId={`${testId}-mic`} disabled={busy} className="absolute right-2 top-2" onText={(t) => setInstruction((v) => (v ? v + " " : "") + t)} />
                        </div>
                        {section === "salaires" && <p className="text-xs text-slate-500">Les noms cités dans l&apos;instruction sont repris à l&apos;identique (aucune reformulation par l&apos;IA).</p>}
                    </div>
                    {busy && (
                        <div className="space-y-1.5" data-testid={`${testId}-progress`}>
                            <div className="flex items-center justify-between text-xs text-slate-600">
                                <span data-testid={`${testId}-progress-label`}>{prog?.total ? `Analyse en cours : Document ${prog.done} / ${prog.total}…` : "Préparation de l'analyse…"}</span>
                                {pct != null && <span className="tabular-nums font-medium text-indigo-700">{pct}%</span>}
                            </div>
                            <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-200">
                                <div className="h-full rounded-full bg-indigo-600 transition-[width] duration-300" style={{ width: pct != null ? `${pct}%` : "15%" }} />
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => setOpen(false)} disabled={busy} className="rounded-full">Annuler</Button>
                        <Button type="button" data-testid={`${testId}-run`} onClick={run} disabled={busy} className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground">
                            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} {busy ? (prog?.total ? `Analyse ${prog.done}/${prog.total}…` : "Extraction en cours…") : (hasSelector && selected.size > 0 ? `Lancer (${selected.size} fichier${selected.size > 1 ? "s" : ""})` : "Lancer")}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}
